<template>
  <el-row :gutter="8">
    <el-col
      :span="4"
      style="background-color: var(--el-border-color-light); border-radius: 6px 0 0 6px"
    >
      <el-row align="middle">
        <el-col :span="7">
          <IconClipboardText style="font-size: 16px; margin-left: 8px" />
        </el-col>

        <el-col
          :span="17"
          style="padding: 4px 0"
        >
          <div style="display: flex; align-items: center; justify-content: center; height: 36px">
            <CustomData
              :data="order.codigoPedido"
              :spanColumn="24"
              style="font-weight: bold; font-size: 0.8rem; text-align: center"
            />
          </div>

          <CustomInputNumber
            :model-value="order.cantidad"
            :spanColumn="24"
            :settings="{
              placeholder: 'Cantidad',
              min: 0,
              max: order.cantidadSaldo,
              precision: 2,
              controls: false,
            }"
            @update:modelValue="handleCantidadChange"
          />
        </el-col>
      </el-row>
    </el-col>

    <el-col
      :span="20"
      style="padding: 4px 0"
    >
      <el-row :gutter="8">
        <el-col :span="15">
          <CustomInput
            :model-value="order.descripcionProducto"
            :spanColumn="24"
            :settings="{ placeholder: 'Descripción producto', disabled: true }"
            style="margin-bottom: 4px"
          />

          <el-row
            :gutter="22"
            style="padding-left: 4px"
          >
            <CustomSearchAutocomplete
              v-model="localProducto"
              :spanColumn="17"
              :minChars="3"
              :fieldKey="'Codigo'"
              :fieldLabel="'DescripcionComercial'"
              :settings="{
                placeholder: 'Ingresar el producto',
                clearable: true,
                size: 'default',
                disabled: productoDisabled,
              }"
              :pagination="{
                enabled: true,
                page: 1,
                limit: 250,
              }"
              @search="handleProductoSearch"
              @select="handleProductoSelect"
              @clear="handleProductoClear"
            />
            <el-col
              v-if="!productoDisabled"
              :span="6"
              style="display: flex; align-items: flex-end; padding-bottom: 2px"
            >
              <el-button
                type="primary"
                size="default"
                @click="goTo('page/material/edit')"
                >Agregar nuevo</el-button
              >
            </el-col>
          </el-row>
        </el-col>

        <el-col :span="7">
          <el-row
            :gutter="8"
            style="padding-left: 8px"
          >
            <CustomSearchAutocomplete
              v-model="localEspecificacionTecnica"
              :spanColumn="16"
              :minChars="1"
              :fieldKey="'Codigo'"
              :fieldLabel="'Codigo'"
              :settings="{
                placeholder: 'Ingresar la especificación técnica',
                clearable: true,
                size: 'default',
                disabled: especificacionTecnicaDisabled,
              }"
              :pagination="{
                enabled: true,
                page: 1,
                limit: 250,
              }"
              @search="handleEspecificacionTecnicaSearch"
              @select="handleEspecificacionTecnicaSelect"
              @clear="handleEspecificacionTecnicaClear"
              @update:modelValue="handleEspecificacionTecnicaUpdate"
            />

            <el-col
              v-if="!especificacionTecnicaDisabled"
              :span="8"
              style="display: flex; align-items: flex-end; padding-bottom: 2px"
            >
              <el-button
                type="primary"
                size="default"
                @click="goTo('page/technical-specifications/edit?from-order-import')"
                >Crear ET</el-button
              >
            </el-col>
          </el-row>
        </el-col>
      </el-row>
    </el-col>
  </el-row>
</template>

<script setup>
import {
  onMounted,
  ref,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useMaterialStore } from '@/stores/materiales_store.js';
import { useElementoGrupoStore } from '@/stores/elemento_grupo/elemento_grupo_store';
import { useTechnicalSpecificationStore } from '@/views/technical_specifications/stores/technical_specification_store.js';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
import CustomInput from '@/shared/components/custom_input.vue';
import CustomData from '@/shared/components/custom_data.vue';
import CustomSearchAutocomplete from '@/shared/components/custom_search_autocomplete.vue';
import IconClipboardText from '~icons/mdi/clipboard-text';

const materialStore = useMaterialStore();
const { searchProductoTerminadoHTTP } = materialStore;

const technicalSpecificationStore = useTechnicalSpecificationStore();
const { searchTechnicalSpecificationHTTP } = technicalSpecificationStore;

const elementoGrupoStore = useElementoGrupoStore();
const { elementoGrupo } = storeToRefs(elementoGrupoStore);

const emit = defineEmits(['cantidad-change', 'producto-change', 'especificacion-tecnica-change']);

const props = defineProps({
  order: { type: Object, required: true },
});

const localProducto = ref(null);
const localEspecificacionTecnica = ref(null);
const localEspecificacionTecnicaSelected = ref(null);
const productoDisabled = ref(null);
const especificacionTecnicaDisabled = ref(null);

watch(localProducto, (newValue, oldValue) => {
  if (
    oldValue &&
    oldValue.DescripcionComercial &&
    oldValue.DescripcionComercial.length >= 1 &&
    (!newValue || !newValue.DescripcionComercial || newValue.DescripcionComercial === '')
  ) {
    handleProductoClear();
  }
});

const handleCantidadChange = (cantidad) => {
  emit('cantidad-change', { order: props.order, cantidad });
};

const handleProductoSearch = async (searchParams, callback) => {
  let results = [];
  if (searchParams.query && searchParams.query.length >= 3 && searchParams.query !== 'null') {
    results = await searchProductoTerminadoHTTP(searchParams.query);
  }
  callback(results || []);
};

const handleProductoClear = () => {
  localProducto.value = null;
  emit('producto-change', { order: props.order, producto: null });
};

const handleProductoSelect = (producto) => {
  const lastEt = producto.EspecificacionTecnicaProducto.filter(
    (et) => et.EspecificacionTecnica.FechaCreacion,
  )
    .sort(
      (a, b) =>
        new Date(a.EspecificacionTecnica.FechaCreacion).getTime() -
        new Date(b.EspecificacionTecnica.FechaCreacion).getTime(),
    )
    .reverse()[0];

  if (lastEt && lastEt.EspecificacionTecnica) {
    localEspecificacionTecnica.value = lastEt.EspecificacionTecnica;
    localEspecificacionTecnicaSelected.value = lastEt.EspecificacionTecnica;
  }
  emit('producto-change', { order: props.order, producto });
};

watch(localEspecificacionTecnica, (newValue, oldValue) => {
  if (
    oldValue &&
    oldValue.Referencia &&
    oldValue.Referencia.length >= 1 &&
    (!newValue || !newValue.Referencia || newValue.Referencia === '')
  ) {
    handleEspecificacionTecnicaClear();
  }
});

const handleEspecificacionTecnicaSearch = async (searchParams, callback) => {
  let results = [];
  if (searchParams.query && searchParams.query !== 'null') {
    results = await searchTechnicalSpecificationHTTP(searchParams.query);
  }
  callback(results || []);
};

const handleEspecificacionTecnicaSelect = (especificacionTecnica) => {
  localEspecificacionTecnicaSelected.value = especificacionTecnica;
  emit('especificacion-tecnica-change', {
    order: props.order,
    especificacionTecnica,
  });
};

const handleEspecificacionTecnicaUpdate = (especificacionTecnica) => {
  localEspecificacionTecnica.value = especificacionTecnica;
  emit('especificacion-tecnica-change', {
    order: props.order,
    especificacionTecnica,
  });
};

const handleEspecificacionTecnicaClear = () => {
  localEspecificacionTecnica.value = null;
  localEspecificacionTecnicaSelected.value = null;
  emit('especificacion-tecnica-change', { order: props.order, especificacionTecnica: null });
};

const goTo = (route) => {
  if (route.includes('technical-specifications') && props.order.ElementoGrupo) {
    localStorage.setItem('tipoProductoSeleccionado', JSON.stringify(props.order.ElementoGrupo));
    elementoGrupo.value = JSON.parse(JSON.stringify(props.order.ElementoGrupo));
  }
  const currentUrl = window.location.origin;
  window.open(`${currentUrl}/${route}`);
};

onMounted(() => {
  localProducto.value = props.order.Producto;
  localEspecificacionTecnica.value = props.order.EspecificacionTecnica;
  productoDisabled.value = props.order.Producto && props.order.Producto.Codigo > 0;
  especificacionTecnicaDisabled.value =
    props.order.EspecificacionTecnica && props.order.EspecificacionTecnica.Codigo > 0;
});
</script>
